import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AutoRedemptionSettingFacade } from '@features/auto-redemption-settings/facades/auto-redemption-setting/auto-redemption-setting.facade';
import { PointsTransferFacade } from '@features/points-transfer/facades/points-transfer.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { getAuthIsLoggedIn } from '@rd/core//auth/store/selectors/auth.selectors';
import { FeatureFlagService } from '@rd/core//services/feature-flag/feature-flag.service';
import { CoreState } from '@rd/core//store/reducers';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { IconTheme, LoyaltyProgram, Membership } from '@rd/types';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { combineLatestWith, map } from 'rxjs/operators';
import {
  CheckoutLinkedAccountPosition,
  CheckoutSubmitButtonPosition,
  TransferConnectV2SettingService,
} from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { TransferConnectV2CheckoutFormData } from '../../types';

type ComponentRenderState = 'content' | 'loading';

@UntilDestroy()
@Component({
  selector: 'rd-tcv2-checkout-form',
  templateUrl: './checkout-form.component.html',
  styleUrls: ['./checkout-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TcV2CheckoutFormComponent implements OnInit {
  @HostBinding('class.rd-tcv2-checkout-form') componentClass = true;
  @Input() formData: TransferConnectV2CheckoutFormData;
  @Input() disabled: boolean;
  @Input() isLoggedIn: boolean;
  @Input() linkedAccount: Membership;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() loyaltyProgramOrMembershipsLoading: boolean;
  @Output() submitForm = new EventEmitter<
    Partial<TransferConnectV2CheckoutFormData>
  >();
  // Static: false, as expandableContent changes because we wait for loyaltyProgram to load
  @ViewChild('expandableContent', { static: false })
  expandableContent: ElementRef;

  checkoutForm: UntypedFormGroup;
  collapsibleTransferAmountForm: boolean;
  expanded = false;
  isSubmitted$: Subject<boolean> = new Subject();
  isLoggedIn$: Observable<boolean | null> =
    this.store.select(getAuthIsLoggedIn);
  linkedAccountPosition: CheckoutLinkedAccountPosition;
  shouldRenderEnrollmentLinkInCheckoutForm: boolean;
  isAutoRedemptionEnabled = this.featureFlagService.isAutoRedemptionEnabled;
  submitButtonPosition: CheckoutSubmitButtonPosition;

  currencyConversionLoading$ = new BehaviorSubject<boolean>(false);
  isDebounceLoading$ = new BehaviorSubject<boolean>(false);
  loadingState$: Observable<boolean> = this.currencyConversionLoading$.pipe(
    untilDestroyed(this),
    combineLatestWith(this.isDebounceLoading$),
    map(
      ([currencyConversionLoading, isDebounceLoading]) =>
        currencyConversionLoading || isDebounceLoading,
    ),
  );

  get autoRedemptionEnabled$(): Observable<boolean> {
    return this.autoRedemptionSettingFacade.autoRedemptionRulesMap$.pipe(
      map(
        (autoRedemptionSettingRulesMap) =>
          autoRedemptionSettingRulesMap[
            this.loyaltyProgram.transferConnectIdentifier
          ],
      ),
      map((value) => {
        if (!value) {
          return true;
        }

        return value.enabled;
      }),
    );
  }

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private iconRegistryService: IconRegistryService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
    private store: Store<CoreState>,
    private autoRedemptionSettingFacade: AutoRedemptionSettingFacade,
    private featureFlagService: FeatureFlagService,
    @Optional() private pointsTransferFacade?: PointsTransferFacade,
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.registerIcons();
    this.collapsibleTransferAmountForm =
      this.transferConnectV2SettingService.collapsibleTransferAmountForm;
    this.linkedAccountPosition =
      this.transferConnectV2SettingService.linkedAccountPosition;
    this.shouldRenderEnrollmentLinkInCheckoutForm =
      this.transferConnectV2SettingService.shouldRenderEnrollmentLinkInCheckoutForm;
    this.submitButtonPosition =
      this.transferConnectV2SettingService.submitButtonPosition;

    if (this.formData || !this.collapsibleTransferAmountForm) {
      this.expanded = true;
    }
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.loyaltyProgram) {
      return 'content';
    } else {
      return 'loading';
    }
  }

  private initForms(): void {
    this.checkoutForm = this.fb.group({
      transferAmountForm: [''],
    });
  }

  get shouldRenderCheckoutButton(): boolean {
    return (
      (this.collapsibleTransferAmountForm || !!this.linkedAccount) &&
      !this.isAutoRedemptionEnabled
    );
  }

  get shouldRenderEnrollmentText(): boolean {
    // @ts-ignore
    return (
      this.shouldRenderEnrollmentLinkInCheckoutForm &&
      !this.linkedAccount &&
      !!this.loyaltyProgram &&
      !!this.loyaltyProgram.pointsTransferCapability.enrollmentUrl
    );
  }

  get shouldRenderLinkedAccountOnTop(): boolean {
    return this.linkedAccountPosition === 'top';
  }

  get shouldRenderLinkedAccountOnBottom(): boolean {
    return this.linkedAccountPosition === 'bottom';
  }

  get shouldRenderLinkedAccountOnOutside(): boolean {
    return this.linkedAccountPosition === 'outside';
  }

  get shouldRenderSubmitButtonInsideForm(): boolean {
    return this.submitButtonPosition === 'inside';
  }

  onSubmit(): void {
    if (
      this.currencyConversionLoading$.value ||
      this.isDebounceLoading$.value ||
      this.checkoutForm.invalid
    ) {
      return;
    }
    // Inform child form that the parent form has been submitted, so they mark each of their components as touched
    this.isSubmitted$.next(true);

    const { sourceAmount, targetAmount } =
      this.checkoutForm.controls.transferAmountForm.value;

    this.submitForm.emit({ sourceAmount, targetAmount });
  }

  redirectToTcAutoRedeemPage(): void {
    if (
      this.currencyConversionLoading$.value ||
      this.isDebounceLoading$.value
    ) {
      return;
    }

    //
    const { sourceAmount, targetAmount } =
      this.checkoutForm.controls.transferAmountForm.value;
    this.pointsTransferFacade?.submitPointsTransferForm({
      loyaltyProgramId: this.loyaltyProgram.id,
      formData: {
        membershipId: '', // Hardcode this for now since we don't have a membershipId
        sourceAmount,
        targetAmount,
      },
    });

    this.router.navigate([
      `transfer/checkout/auto-redemption/membership/${this.loyaltyProgram.id}`,
    ]);
  }

  iconTheme(): IconTheme | null {
    if (this.disabled) {
      return 'disabled';
    } else {
      return 'link';
    }
  }

  toggleExpansion(): void {
    if (this.disabled) {
      return;
    }

    this.expanded = !this.expanded;
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('arrow-down');
  }

  setCurrencyConversionLoading(loading: boolean): void {
    this.currencyConversionLoading$.next(loading);
  }

  setDebounceLoading(loading: boolean): void {
    this.isDebounceLoading$.next(loading);
  }

  /* ========= */
}
